/**
 * チュートリアルミッション (従業員向けの使い方を、画面を操作しながら覚えるミッション)。
 *
 * - 中身は使い方ガイド (src/components/guide/EmployeeGuideContent.tsx) と
 *   ホームの使い方ツアー (src/app/(app)/home/components/HomeTour.tsx) で教えている操作。
 *   **操作 1 つ = ミッション 1 つ**。文言のボタン名・タブ名は画面の表記と一致させること。
 * - 1 人 1 回ずつクリアでき、クリアすると reward のポイントがもらえる (ショップ残高。EXP ではない)。
 *   記録は point_earn_logs (source = "tutorial", ref_id = code) に 1 行。専用テーブルは作らない
 *   (dev / 本番共有 DB の手動 migration を増やさないため)。
 * - クリアの判定:
 *   - clear: "auto"   … アカウント登録の 3 つ。ミッション一覧を開いたとき、サーバーが登録状況を確かめてクリアにする
 *   - clear: "action" … 画面で操作が成功したときにアプリが知らせる (reportTutorialAction)。
 *                       画面を開くだけのミッションは TutorialMissionHost が URL を見て知らせる
 *
 * 教えないことにした操作 (ミッションにしない):
 *   - ログアウト        … ミッションにするとセッションが切れるだけで、覚えることがない
 *   - AI 改善窓口で依頼する … 依頼は自動で実装・反映されるため、ポイント目当ての依頼を生まない (開くだけをミッションにする)
 *   - 収益化ミッションの申請・カードの使用・トレード … 管理者の承認や相手が要り、1 人では終えられない
 *
 * code は point_earn_logs.ref_id に保存する永続 ID。**一度使った code は変えない・使い回さない**。
 */

export const TUTORIAL_MISSION_SOURCE = "tutorial" as const;

/** 画面のどこを光らせるか (data-tour 属性は各画面の部品に付けている) */
export const TUTORIAL_TARGETS = {
  navHome: 'nav button[aria-label="ホーム"]',
  navStamp: 'nav button[aria-label="打刻"]',
  navMembers: 'nav button[aria-label="メンバー"]',
  navShop: 'nav button[aria-label="ショップ"]',
  navCards: 'nav button[aria-label="カード"]',
  navMypage: 'nav button[aria-label="マイページ"]',
  helpMenu: '[data-tour="help-menu"]',
  helpMenuGuide: '[data-tour="help-menu-guide"]',
  helpMenuImprove: '[data-tour="help-menu-improve"]',
  homeEarnings: '[data-tour="home-earnings"]',
  homeTreasure: '[data-tour="home-treasure"]',
  homeMissionTabs: '[data-tour="home-mission-tabs"]',
  homeMissionComplete: '[data-tour="mission-complete"]',
  homeAffiliateLink: '[data-tour="home-affiliate-link"]',
  stampClockIn: '[data-tour="stamp-clock-in"]',
  stampClockOut: '[data-tour="stamp-clock-out"]',
  stampGacha: '[data-tour="stamp-gacha"]',
  membersList: '[data-tour="members-list"]',
  thankyouSend: '[data-tour="thankyou-send"]',
  membersGraphTab: 'button[data-segment-tab="相関図"]',
  membersRankingTab: 'button[data-segment-tab="ランキング"]',
  shopItems: '[data-tour="shop-items"]',
  /** 交換の確認ダイアログ (Modal.confirm) の「交換する」 */
  shopConfirmOk: ".ant-modal-confirm-btns .ant-btn-primary",
  shopHistoryTab: 'button[data-segment-tab="購入履歴"]',
  cardsWorld: 'button[aria-label="現在 WORLD"]',
  cardsInventoryTab: '.ant-tabs-tab[data-node-key="inventory"]',
} as const;

export interface TutorialStep {
  title: string;
  body: string;
  /**
   * この手順を行う画面 (例: "/stamp")。null はどの画面でもよい (下のメニュー・右上の「？」など)。
   * path も target も null の手順は自動では進まないので、案内の「次へ」で進める。
   */
  path: string | null;
  /** 光らせる場所 (CSS セレクタ)。画面に出ていればその手順まで自動で進む */
  target: string | null;
  /** この画面にいるのに target が見つからないときの案内 (例: 宝箱は出勤中だけ出る) */
  missingNote?: string;
}

export type TutorialChapterKey = "start" | "daily" | "members" | "growth" | "help";

export const TUTORIAL_CHAPTERS: readonly { key: TutorialChapterKey; label: string }[] = [
  { key: "start", label: "はじめの一歩" },
  { key: "daily", label: "毎日の流れ" },
  { key: "members", label: "仲間とつながる" },
  { key: "growth", label: "ポイントを使う・成長を見る" },
  { key: "help", label: "困ったとき" },
];

export type TutorialMissionKey =
  | "signup_login"
  | "signup_character"
  | "signup_mbti"
  | "home_tour"
  | "earnings_history"
  | "guide_open"
  | "home_screen"
  | "clock_in"
  | "gacha"
  | "treasure"
  | "mission_complete"
  | "clock_out"
  | "thankyou_send"
  | "members_graph"
  | "members_ranking"
  | "shop_exchange"
  | "shop_history"
  | "cards_inventory"
  | "mypage_open"
  | "affiliate_open"
  | "improve_open";

export interface TutorialMission {
  /** point_earn_logs.ref_id に保存する永続 ID (変えない・使い回さない) */
  code: number;
  key: TutorialMissionKey;
  chapter: TutorialChapterKey;
  title: string;
  /** 何をするミッションか (一覧と案内に出す) */
  summary: string;
  /** クリアでもらえるポイント */
  reward: number;
  clear: "auto" | "action";
  /** 始め方。tour = ホームの使い方ツアーを開く (手順の案内は出さない) */
  start?: "tour";
  /** 実際に記録が残る・取り消せない操作の注意 (案内の手順の下に出す) */
  caution?: string;
  steps: readonly TutorialStep[];
}

const T = TUTORIAL_TARGETS;

const openHome: TutorialStep = {
  title: "「ホーム」を開く",
  body: "画面下のメニューの「ホーム」を押します。",
  path: null,
  target: T.navHome,
};
const openStamp: TutorialStep = {
  title: "「打刻」を開く",
  body: "画面下のメニューの「打刻」を押します。",
  path: null,
  target: T.navStamp,
};
const openMembers: TutorialStep = {
  title: "「メンバー」を開く",
  body: "画面下のメニューの「メンバー」を押します。",
  path: null,
  target: T.navMembers,
};
const openShop: TutorialStep = {
  title: "「ショップ」を開く",
  body: "画面下のメニューの「ショップ」を押します。",
  path: null,
  target: T.navShop,
};
const openHelpMenu: TutorialStep = {
  title: "右上の「？」を押す",
  body: "画面右上の「？」を押すと、メニューが開きます。",
  path: null,
  target: T.helpMenu,
};

const FREEE_CAUTION =
  "打刻は freee 人事労務の勤怠に記録されます。ミッションのためではなく、実際の出勤・退勤のときに押してください。";

export const TUTORIAL_MISSIONS: readonly TutorialMission[] = [
  // ── はじめの一歩 (アカウント登録とアプリの基本) ─────────────────
  {
    code: 1,
    key: "signup_login",
    chapter: "start",
    title: "freee でログインする",
    summary:
      "「ログイン」を押し、freee でログインして連携を許可します。アカウント登録が済んでいれば自動でクリアになります。",
    reward: 10,
    clear: "auto",
    steps: [],
  },
  {
    code: 2,
    key: "signup_character",
    chapter: "start",
    title: "キャラクターを選ぶ",
    summary:
      "はじめてのログインで、気に入ったキャラクターを選んで「決定」を押します。選び終わっていれば自動でクリアになります。",
    reward: 10,
    clear: "auto",
    steps: [],
  },
  {
    code: 3,
    key: "signup_mbti",
    chapter: "start",
    title: "16 タイプ（MBTI）を選ぶ",
    summary:
      "自分のタイプを 1 つ選んで「決定」を押します。選んだタイプに対応する 2 つの能力のミッションは、報酬が 10% 増えます。登録が済んでいれば自動でクリアになります。",
    reward: 10,
    clear: "auto",
    steps: [],
  },
  {
    code: 4,
    key: "home_tour",
    chapter: "start",
    title: "ホームの使い方ツアーを最後まで見る",
    summary:
      "ホームの見方を順番に案内します。「次へ」で進み、最後の「はじめる」まで進めるとクリアです。",
    reward: 10,
    clear: "action",
    start: "tour",
    steps: [],
  },
  {
    code: 5,
    key: "earnings_history",
    chapter: "start",
    title: "今日の加算履歴を開く",
    summary:
      "ホームの「今日の獲得」をタップすると、今日もらったポイントと EXP の内訳が開きます。",
    reward: 10,
    clear: "action",
    steps: [
      openHome,
      {
        title: "「今日の獲得」をタップする",
        body: "ホームのいちばん上のカードをタップします。「月間合計」になっているときは、「今日のポイント」に切り替えてからタップしてください。今日の加算履歴が開いたらクリアです。",
        path: "/home",
        target: T.homeEarnings,
      },
    ],
  },
  {
    code: 6,
    key: "guide_open",
    chapter: "start",
    title: "「？」から使い方ガイドを開く",
    summary:
      "画面右上の「？」から、ホームの使い方ツアーとくわしい使い方ガイドをいつでも見返せます。",
    reward: 10,
    clear: "action",
    steps: [
      openHelpMenu,
      {
        title: "「使い方ガイド」を選ぶ",
        body: "メニューの「使い方ガイド」を押します。ガイドが開いたらクリアです。",
        path: null,
        target: T.helpMenuGuide,
      },
    ],
  },
  {
    code: 7,
    key: "home_screen",
    chapter: "start",
    title: "スマホのホーム画面に追加する",
    summary:
      "ホーム画面に追加すると、アイコンからアドレスバーのない全画面で開けます。追加したアイコンからアプリを開くとクリアです。",
    reward: 30,
    clear: "action",
    steps: [
      {
        title: "iPhone（Safari）の場合",
        body: "このアプリを Safari で開いた状態で、画面下の共有ボタン（□ に ↑ のアイコン）→「ホーム画面に追加」→「追加」を押します。",
        path: null,
        target: null,
      },
      {
        title: "Android（Chrome）の場合",
        body: "このアプリを Chrome で開いた状態で、右上の「︙」→「ホーム画面に追加」（または「アプリをインストール」）を押します。",
        path: null,
        target: null,
      },
      {
        title: "追加したアイコンから開く",
        body: "スマホのホーム画面にできた GROWTH:CODE のアイコンからアプリを開くとクリアです。ログイン画面が出たら、もう一度ログインしてください。表示や名前は、機種やブラウザによって少し異なります。",
        path: null,
        target: null,
      },
    ],
  },

  // ── 毎日の流れ (打刻・ガチャ・宝箱・ミッション) ──────────────────
  {
    code: 8,
    key: "clock_in",
    chapter: "daily",
    title: "出勤を打刻する",
    summary:
      "仕事を始めるときに「出勤」を押します。出勤するとガチャチケットが 1 枚もらえ、出勤中は 15 分ごとに EXP がたまります。",
    reward: 20,
    clear: "action",
    caution: FREEE_CAUTION,
    steps: [
      openStamp,
      {
        title: "「出勤」を押す",
        body: "仕事を始めるときに「出勤」を押します。押せないときは、もう出勤済みです（次の出勤のときにクリアできます）。",
        path: "/stamp",
        target: T.stampClockIn,
      },
    ],
  },
  {
    code: 9,
    key: "gacha",
    chapter: "daily",
    title: "ガチャを回す",
    summary:
      "ガチャチケット 1 枚でガチャを回すと、ポイントとカードが手に入ります。チケットは出勤・退勤のたびに 1 枚もらえます。",
    reward: 10,
    clear: "action",
    steps: [
      openStamp,
      {
        title: "「ガチャを回す」を押す",
        body: "「所持チケット数」の下の「ガチャを回す」を押します。チケットが 0 枚のときは押せないので、出勤してチケットをもらってからためしてください。",
        path: "/stamp",
        target: T.stampGacha,
      },
    ],
  },
  {
    code: 10,
    key: "treasure",
    chapter: "daily",
    title: "宝箱を開ける",
    summary:
      "出勤中は、ホームの宝箱にポイントがたまります。「宝箱を開ける」で受け取れます（開けなくても退勤時に自動で回収されます）。",
    reward: 10,
    clear: "action",
    steps: [
      openHome,
      {
        title: "「宝箱を開ける」を押す",
        body: "ホームの宝箱の「宝箱を開ける」を押します。自分で開けると、ボーナスが付くことがあります。",
        path: "/home",
        target: T.homeTreasure,
        missingNote:
          "宝箱は出勤中にホームに出ます。出勤して、ポイントがたまってからためしてください。",
      },
    ],
  },
  {
    code: 11,
    key: "mission_complete",
    chapter: "daily",
    title: "ミッションを完了する",
    summary:
      "ホームの「ミッション」の「デイリー」「ウィークリー」「マンスリー」に、取り組むミッションが出ます。取り組んだら「完了」を押します（完了は自己申告です）。",
    reward: 10,
    clear: "action",
    steps: [
      openHome,
      {
        title: "ミッションの種類を選ぶ",
        body: "ホームの「ミッション」で「デイリー」を押します（「ウィークリー」「マンスリー」でもかまいません）。どのタブにも「完了」が無いときは、次のミッションが出てからためしてください。",
        path: "/home",
        target: T.homeMissionTabs,
      },
      {
        title: "取り組んだら「完了」を押す",
        body: "ミッションに取り組んだら、そのミッションの「完了」を押します。ポイントやガチャチケットなどの報酬がもらえます。",
        path: "/home",
        target: T.homeMissionComplete,
      },
    ],
  },
  {
    code: 12,
    key: "clock_out",
    chapter: "daily",
    title: "退勤を打刻する",
    summary:
      "仕事を終えるときに「退勤」を押します。退勤するとガチャチケットが 1 枚もらえ、宝箱にたまったポイントも自動で受け取れます。",
    reward: 20,
    clear: "action",
    caution: FREEE_CAUTION,
    steps: [
      openStamp,
      {
        title: "「退勤」を押す",
        body: "仕事を終えるときに「退勤」を押します。出勤していないときは押せません。",
        path: "/stamp",
        target: T.stampClockOut,
      },
    ],
  },

  // ── 仲間とつながる (メンバー) ──────────────────────────────────
  {
    code: 13,
    key: "thankyou_send",
    chapter: "members",
    title: "Thank you を送る",
    summary:
      "仲間に感謝のポイント（1〜100 pt）と理由を送ります。送ったポイントは相手に付与され、あなたのポイントは減りません。",
    reward: 20,
    clear: "action",
    steps: [
      openMembers,
      {
        title: "相手を選ぶ",
        body: "「メンバー」のタブで、Thank you を送りたい相手を押します。",
        path: "/members",
        target: T.membersList,
        missingNote: "上のタブの「メンバー」を押すと、仲間の一覧が出ます。",
      },
      {
        title: "ポイントと理由を入れて送る",
        body: "「ThankYouポイントを送る」に 1〜100 pt と理由コメントを入れて「送信する」を押します。",
        path: "/members",
        target: T.thankyouSend,
      },
    ],
  },
  {
    code: 14,
    key: "members_graph",
    chapter: "members",
    title: "相関図を見る",
    summary: "メンバーの「相関図」で、Thank you のつながりを図で見られます。",
    reward: 10,
    clear: "action",
    steps: [
      openMembers,
      {
        title: "「相関図」を押す",
        body: "上のタブの「相関図」を押します。",
        path: "/members",
        target: T.membersGraphTab,
      },
    ],
  },
  {
    code: 15,
    key: "members_ranking",
    chapter: "members",
    title: "ランキングを見る",
    summary: "メンバーの「ランキング」で、Thank you の順位を見られます。",
    reward: 10,
    clear: "action",
    steps: [
      openMembers,
      {
        title: "「ランキング」を押す",
        body: "上のタブの「ランキング」を押します。",
        path: "/members",
        target: T.membersRankingTab,
      },
    ],
  },

  // ── ポイントを使う・成長を見る ─────────────────────────────────
  {
    code: 16,
    key: "shop_exchange",
    chapter: "growth",
    title: "ショップで商品と交換する",
    summary:
      "ためたポイントを商品と交換します。交換したら、スタッフに声をかけてください。",
    reward: 30,
    clear: "action",
    caution:
      "交換したポイントは戻りません。ほしい商品があって、ポイントがたまったときに交換してください。",
    steps: [
      openShop,
      {
        title: "商品の「購入する」を押す",
        body: "「商品依頼」のタブで、交換したい商品の「購入する」を押します。",
        path: "/shop",
        target: T.shopItems,
        missingNote: "上のタブの「商品依頼」を押すと、商品の一覧が出ます。",
      },
      {
        title: "「交換する」を押す",
        body: "確認の画面で「交換する」を押すと、ポイントと交換できます。交換したら、スタッフに声をかけてください。",
        path: "/shop",
        target: T.shopConfirmOk,
      },
    ],
  },
  {
    code: 17,
    key: "shop_history",
    chapter: "growth",
    title: "購入履歴を見る",
    summary: "ショップの「購入履歴」で、交換した記録を確認できます。",
    reward: 10,
    clear: "action",
    steps: [
      openShop,
      {
        title: "「購入履歴」を押す",
        body: "上のタブの「購入履歴」を押します。",
        path: "/shop",
        target: T.shopHistoryTab,
      },
    ],
  },
  {
    code: 18,
    key: "cards_inventory",
    chapter: "growth",
    title: "所持カードを見る",
    summary:
      "ガチャなどで手に入れたカードは「カード」で確認できます。いま持っているカードは「現在」の「所持カード」にあります。",
    reward: 10,
    clear: "action",
    steps: [
      {
        title: "「カード」を開く",
        body: "画面下のメニューの「カード」を押します。",
        path: null,
        target: T.navCards,
      },
      {
        title: "「現在」を押す",
        body: "「過去」「現在」「未来」のうち「現在」を押します。",
        path: "/cards",
        target: T.cardsWorld,
      },
      {
        title: "「所持カード」を押す",
        body: "上のタブの「所持カード」を押します。カードの使用や、仲間とのトレードもこの画面からできます。",
        path: "/cards/world",
        target: T.cardsInventoryTab,
      },
    ],
  },
  {
    code: 19,
    key: "mypage_open",
    chapter: "growth",
    title: "マイページで成長を見る",
    summary:
      "マイページでは、レベルと次のレベルまでの EXP、能力値、成長グラフ、これまでの履歴を確認できます。",
    reward: 10,
    clear: "action",
    steps: [
      {
        title: "「マイページ」を開く",
        body: "画面下のメニューの「マイページ」を押します。マイページが開いたらクリアです。",
        path: null,
        target: T.navMypage,
      },
    ],
  },
  {
    code: 20,
    key: "affiliate_open",
    chapter: "growth",
    title: "収益化ミッションを見る",
    summary:
      "参加が任意の収益化ミッションの一覧です。気になる案件があれば、その画面から申請できます。",
    reward: 10,
    clear: "action",
    steps: [
      openHome,
      {
        title: "「収益化ミッションを見る →」を押す",
        body: "ホームの「ミッション」の右下にある「収益化ミッションを見る →」を押します。一覧が開いたらクリアです。",
        path: "/home",
        target: T.homeAffiliateLink,
      },
    ],
  },

  // ── 困ったとき ──────────────────────────────────────────────
  {
    code: 21,
    key: "improve_open",
    chapter: "help",
    title: "AI 改善窓口を開く",
    summary:
      "「ここが分かりにくい」「こうしてほしい」と思ったら、AI 改善窓口から伝えられます。開くだけでクリアです（依頼を送る必要はありません）。",
    reward: 10,
    clear: "action",
    steps: [
      openHelpMenu,
      {
        title: "「AI 改善窓口（要望・不具合）」を選ぶ",
        body: "メニューの「AI 改善窓口（要望・不具合）」を押します。窓口が開いたらクリアです。",
        path: null,
        target: T.helpMenuImprove,
      },
    ],
  },
];

const BY_KEY = new Map<string, TutorialMission>(
  TUTORIAL_MISSIONS.map((m) => [m.key, m]),
);
const BY_CODE = new Map<number, TutorialMission>(
  TUTORIAL_MISSIONS.map((m) => [m.code, m]),
);

export function getTutorialMission(key: string): TutorialMission | undefined {
  return BY_KEY.get(key);
}

export function getTutorialMissionByCode(code: number): TutorialMission | undefined {
  return BY_CODE.get(code);
}

export function isTutorialMissionKey(key: string): key is TutorialMissionKey {
  return BY_KEY.has(key);
}

/** 全部クリアしたときにもらえるポイントの合計 */
export const TUTORIAL_TOTAL_REWARD = TUTORIAL_MISSIONS.reduce(
  (sum, m) => sum + m.reward,
  0,
);

/**
 * 画面を開いただけでクリアになるミッション (TutorialMissionHost が URL を見て知らせる)。
 * 例: /mypage を開いたら mypage_open
 */
export const TUTORIAL_PAGE_VISIT_MISSIONS: readonly {
  path: string;
  key: TutorialMissionKey;
}[] = [
  { path: "/guide", key: "guide_open" },
  { path: "/mypage", key: "mypage_open" },
  { path: "/affiliate-missions", key: "affiliate_open" },
  { path: "/improve", key: "improve_open" },
];
